import React, { useEffect, useRef, useState } from "react";
import {
  HeartHandshake, ShieldPlus, BarChart3, Award, Microscope,
  Info, CheckCircle2, Link2, User, ChevronLeft, ChevronRight, Lock, Sparkles, Mic, Send,
  Ear, Building2, Radio, RotateCcw,
} from "lucide-react";

/**
 * One private experience. Five pathways into ANA support.
 * The phone contains only nurse-facing language. All institutional framing
 * (evidence, reporting, research, aggregate patterns) lives in the panels
 * outside the phone.
 */

const programs = [
  {
    key: "H",
    name: "Healthy Nurse, Healthy Nation",
    benefit: "Whole-person wellbeing",
    icon: HeartHandshake,
    color: "#2dd4bf",
    whatIs: "A national nurse wellness platform and engagement community supporting mental health, physical activity, rest, nutrition, quality of life, and safety.",
    accomplishes: "Offers resources, challenges, community participation, and practical opportunities for nurses and organizations to support healthier daily choices.",
    connects: "My Mood Groove turns broad wellbeing priorities into a personal next step, connecting the nurse with the most relevant Healthy Nurse, Healthy Nation resource when a need is recognized.",
    example: "After a demanding shift, a nurse chooses a post-shift decompression experience. My Mood Groove helps identify whether the immediate need is rest, emotional space, physical recovery, or additional support.",
    moment: {
      setting: "After a 12-hour ICU night",
      chat: [
        { from: "ai", text: "That sounded like a demanding shift." },
        { from: "ai", text: "What feels most present right now: your body, your thoughts, or your emotions?" },
        { from: "nurse", text: "My body. Wired but exhausted." },
        { from: "ai", text: "That makes sense. Your body may still be carrying the pace of the shift." },
        { from: "ai", text: "Would you like a short recovery moment, a sleep transition, or space to settle first?" },
      ],
      chips: ["Physical recovery", "Prepare for rest", "Stay here a moment"],
      nurseExperience: "A private post-shift check-in that helps the nurse recognize what type of recovery may be most helpful.",
      anaReceives: "No conversation or personal response. Only protected, deidentified patterns may indicate which wellbeing resources nurses seek most often.",
      privacy: "The nurse\u2019s words remain private. ANA receives no transcript, identity, or individual activity record.",
      connectionPanel: "The nurse can be guided toward a relevant Healthy Nurse, Healthy Nation resource based on the need identified in the moment.",
    },
  },
  {
    key: "N",
    name: "Nurse Well-Being",
    benefit: "Recognize and respond to stress",
    icon: ShieldPlus,
    color: "#60a5fa",
    whatIs: "Evidence-based resources using the Stress First Aid model to help nurses and leaders recognize stress reactions, improve recovery, and build a shared language around stress and burnout.",
    accomplishes: "Strengthens peer support and helps nurses connect with appropriate resources earlier.",
    connects: "My Mood Groove provides a private reflective step before, during, or after peer support. It helps the nurse understand the moment and decide whether self-guided support, a Stress First Aid action, or connection with a trained peer feels appropriate.",
    example: "After an upsetting patient event, the platform listens first, helps the nurse recognize what feels unfinished, and offers a choice between staying in the moment, trying a grounding action, or connecting with a trained peer supporter.",
    moment: {
      setting: "After an upsetting patient event",
      chat: [
        { from: "ai", text: "It sounds like this one is still with you." },
        { from: "ai", text: "Would you like to name what feels unfinished, or would it feel better to simply pause here?" },
        { from: "nurse", text: "The moment I could not do more." },
        { from: "ai", text: "Thank you for trusting me with that." },
        { from: "ai", text: "You do not have to solve it right now. Would it help to try a grounding step, stay here for a moment, or connect with a trained peer?" },
      ],
      chips: ["Try a grounding step", "Stay here", "Connect with a peer"],
      nurseExperience: "A private moment to recognize stress, stabilize, and choose the next level of support.",
      anaReceives: "No personal account of the event. Only deidentified patterns may help show which support pathways nurses choose and when additional resources may be needed.",
      privacy: "The nurse\u2019s words remain private. Employers and ANA receive no transcript, identity, or individual nurse profile.",
      connectionPanel: "The experience aligns with Stress First Aid by helping the nurse pause, recognize the moment, and choose support without pressure.",
    },
  },
  {
    key: "F",
    name: "American Nurses Foundation",
    benefit: "Research and innovation",
    icon: Microscope,
    color: "#a78bfa",
    whatIs: "The American Nurses Foundation supports nurse-led innovation, research, charitable initiatives, and programs that advance nursing and improve health.",
    accomplishes: "It helps fund, test, evaluate, and expand promising approaches that may strengthen nurse wellbeing, professional practice, and patient care.",
    connects: "The Foundation could support research design, pilot development, clinical review, evaluation, and evidence building for a nurse-specific private awareness platform.",
    example: "A nurse participating in a voluntary pilot is invited to decide whether a deidentified signal from an experience may contribute to evaluation. Participation remains optional. The nurse's words, identity, employer information, and individual profile remain private.",
    moment: {
      setting: "End of an evening reflection",
      chat: [
        { from: "ai", text: "You are participating in a voluntary nurse wellbeing pilot." },
        { from: "ai", text: "Would you allow a deidentified signal from this experience to contribute to pilot evaluation?" },
        { from: "ai", text: "Your words, identity, employer information, and personal profile will not be shared." },
        { from: "nurse", text: "Yes, share without my identity." },
        { from: "ai", text: "Thank you." },
        { from: "ai", text: "Your conversation remains private. Only a deidentified, combined signal that meets established privacy thresholds may contribute." },
        { from: "ai", text: "You can change this choice at any time." },
      ],
      chips: ["Yes, share without my identity", "Not this time", "Review what may be contributed"],
      nurseExperience: "A clear consent moment explaining what may contribute to evaluation, what remains private, and how to change the choice later.",
      anaReceives: "Only specifically consented, deidentified, combined signals that meet established privacy thresholds. No transcript, identity, employer information, biometric record, or individual emotional profile.",
      privacy: "The nurse owns the conversation. Personal reflections and individual choices remain private. With consent and appropriate safeguards, the pilot may receive only deidentified, aggregated patterns that can help evaluate usefulness and improve resource access.",
      connectionPanel: "The experience can contribute to a Foundation-supported pilot while preserving nurse privacy, voluntary participation, and clear consent.",
    },
  },
  {
    key: "A",
    name: "ANCC Excellence",
    benefit: "Practice environment",
    icon: Award,
    color: "#38bdf8",
    whatIs: "Evidence-based standards that help organizations evaluate wellbeing efforts, identify gaps, establish goals, and demonstrate a supportive professional practice environment.",
    accomplishes: "Improves morale and engagement, strengthens retention, and supports organizational commitment to workforce wellbeing.",
    connects: "My Mood Groove could serve as a practical daily engagement layer within an organization\u2019s workforce wellbeing strategy, producing privacy-protected indicators of participation, expressed needs, and reported change. Any relationship to ANCC standards would require formal review and validation.",
    example: "During a quiet moment between rounds, a nurse privately identifies a current feeling and chooses whether to pause, breathe, or return later. No personal conversation becomes part of an organizational record.",
    moment: {
      setting: "A quiet moment between rounds",
      chat: [
        { from: "ai", text: "How are you carrying tonight, in one word?" },
        { from: "nurse", text: "Stretched." },
        { from: "ai", text: "Thank you. That says a lot." },
        { from: "ai", text: "You do not need to explain more." },
        { from: "ai", text: "Would it help to pause here, take one guided breath, or return when you have more space?" },
      ],
      chips: ["Stay", "Breathe with me", "Return later"],
      nurseExperience: "A private pause that helps the nurse recognize strain and choose a manageable next step.",
      anaReceives: "No words or transcripts. Protected, deidentified patterns may contribute to a broader understanding of workforce wellbeing and engagement.",
      privacy: "Employers receive no transcript, individual nurse profile, or identifiable activity. Organizational insight begins only at protected aggregate thresholds.",
      connectionPanel: "Private reflective use may contribute deidentified, aggregate indicators to broader practice-environment evaluation. Formal alignment with ANCC standards would require review and validation.",
    },
  },
  {
    key: "P",
    name: "Pulse Survey",
    benefit: "Workforce insight",
    icon: BarChart3,
    color: "#fbbf24",
    whatIs: "A national survey series capturing nurses\u2019 perspectives on burnout, mental health, workplace conditions, and changing workforce needs.",
    accomplishes: "Informs ANA and the American Nurses Foundation\u2019s advocacy, policy, research, and program design.",
    connects: "My Mood Groove complements periodic surveys with a voluntary awareness layer that may reveal how nurse needs shift over time while keeping personal reflections private.",
    example: "A nurse does not want to talk. The platform respects the decision, offers an optional future check-in, and records no personal response as a survey answer.",
    moment: {
      setting: "The nurse does not want to talk right now",
      // Stateful chat — each state renders a fresh chip set based on the last choice
      stateful: true,
      states: [
        {
          chat: [
            { from: "ai", text: "You do not need to say anything right now." },
            { from: "ai", text: "Choose what feels right." },
          ],
          chips: [
            { label: "I want to talk", next: 3 },
            { label: "Not tonight", next: 1 },
            { label: "Check on me tomorrow", next: 2 },
          ],
        },
        {
          // after "Not tonight"
          chat: [
            { from: "ai", text: "You do not need to say anything right now." },
            { from: "nurse", text: "Not tonight." },
            { from: "ai", text: "Of course." },
            { from: "ai", text: "I will leave this moment with you. You can return whenever you are ready." },
            { from: "ai", text: "Would you like me to check in tomorrow?" },
          ],
          chips: [
            { label: "Yes, check in tomorrow", next: 2 },
            { label: "No check in", next: 4 },
            { label: "I want to talk now", next: 3 },
          ],
        },
        {
          // after "Yes, check in tomorrow"
          chat: [
            { from: "nurse", text: "Yes, check in tomorrow." },
            { from: "ai", text: "I will check in tomorrow." },
            { from: "ai", text: "Nothing else is needed tonight." },
          ],
          chips: [
            { label: "Close for now", next: null },
            { label: "Change check-in time", next: null },
            { label: "Talk now", next: 3 },
          ],
        },
        {
          // after "I want to talk"
          chat: [
            { from: "nurse", text: "I want to talk." },
            { from: "ai", text: "I\u2019m here. There\u2019s no pace to keep and nothing you must share." },
            { from: "ai", text: "Would you like to start with what feels most present, or take a breath together first?" },
          ],
          chips: [
            { label: "Start with what\u2019s present", next: null },
            { label: "Take a breath first", next: null },
            { label: "Actually, later", next: 1 },
          ],
        },
        {
          // after "No check in"
          chat: [
            { from: "nurse", text: "No check-in." },
            { from: "ai", text: "Understood." },
            { from: "ai", text: "This space is here whenever you decide to return." },
          ],
          chips: [
            { label: "Close for now", next: null },
            { label: "Actually, check in tomorrow", next: 2 },
          ],
        },
      ],
      nurseExperience: "Declining to share is respected as a valid, dignified interaction. The nurse remains in control of whether and when another check-in occurs.",
      anaReceives: "No personal response and no survey record. Deidentified patterns may indicate that some nurses defer engagement at particular times or around particular needs.",
      privacy: "Personal responses never become survey records. Only deidentified trends may help inform future questions and advocacy priorities.",
      connectionPanel: "A deferred need may contribute only as an deidentified trend signal, helping identify areas that could warrant future survey questions or advocacy attention.",
    },
  },
];

/* ---------- Program selector card ---------- */
const SelectorCard = ({ p, isActive, onClick }) => {
  const Icon = p.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`program-selector-${p.key}`}
      aria-pressed={isActive}
      className="relative text-left rounded-2xl p-4 flex items-start gap-3 transition-all duration-300 group"
      style={{
        background: isActive ? `${p.color}1a` : "rgba(255,255,255,0.03)",
        border: `1.5px solid ${isActive ? p.color : "rgba(255,255,255,0.08)"}`,
        boxShadow: isActive
          ? `0 12px 40px ${p.color}33, inset 0 0 30px ${p.color}12, 0 0 0 4px ${p.color}18`
          : "0 4px 20px rgba(4,12,40,0.25)",
        transform: isActive ? "translateY(-2px)" : "translateY(0)",
      }}
    >
      <span
        className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center transition"
        style={{ background: `${p.color}22`, border: `1px solid ${p.color}66`, boxShadow: isActive ? `0 0 24px ${p.color}55` : "none" }}
      >
        <Icon size={20} style={{ color: p.color }} />
      </span>
      <span className="min-w-0 flex-1 pt-0.5">
        <span className="block font-heading text-white text-[13.5px] leading-tight">{p.name}</span>
        <span className="block text-[11.5px] text-zinc-400 leading-snug mt-1">{p.benefit}</span>
      </span>
      {isActive && (
        <>
          <span aria-hidden="true" className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 rotate-45"
                style={{ background: `${p.color}1a`, borderRight: `1.5px solid ${p.color}`, borderBottom: `1.5px solid ${p.color}` }} />
          <span aria-hidden="true"
                className="hidden lg:block absolute left-1/2 -translate-x-1/2 top-full w-px h-8 mt-2 mmc-line"
                style={{ background: `linear-gradient(180deg, ${p.color}, transparent)` }} />
        </>
      )}
    </button>
  );
};

/* ---------- Compact sticky selector ---------- */
const StickySelector = ({ selected, onSelect, visible }) => {
  const p = programs[selected];
  return (
    <div role="tablist" aria-label="Program selector" data-testid="sticky-program-selector"
         className={`fixed left-1/2 -translate-x-1/2 z-40 transition-all duration-500 ease-out ${visible ? "top-4 opacity-100 pointer-events-auto" : "-top-24 opacity-0 pointer-events-none"}`}>
      <div className="flex items-center gap-2 px-3 py-2 rounded-full backdrop-blur-xl border shadow-2xl"
           style={{ background: "rgba(8,17,42,0.85)", borderColor: `${p.color}55`, boxShadow: `0 10px 40px rgba(4,12,40,0.55), 0 0 30px ${p.color}22` }}>
        <span className="hidden sm:inline text-[10px] uppercase tracking-[0.22em] pl-2 pr-1" style={{ color: p.color }}>{p.name}</span>
        <span className="w-px h-5 hidden sm:inline-block" style={{ background: `${p.color}55` }} />
        {programs.map((pp, i) => {
          const Icon = pp.icon;
          const active = i === selected;
          return (
            <button key={pp.key} type="button" role="tab" aria-selected={active}
                    onClick={() => onSelect(i)} data-testid={`sticky-selector-${pp.key}`}
                    className="relative w-9 h-9 rounded-full flex items-center justify-center transition"
                    style={{ background: active ? `${pp.color}30` : "rgba(255,255,255,0.03)", border: `1px solid ${active ? pp.color : "rgba(255,255,255,0.08)"}`, boxShadow: active ? `0 0 18px ${pp.color}66` : "none" }}
                    title={pp.name}>
              <Icon size={15} style={{ color: pp.color }} />
            </button>
          );
        })}
      </div>
    </div>
  );
};

const PanelSection = ({ icon: Icon, title, children, color }) => (
  <div>
    <div className="flex items-center gap-2 mb-2">
      <Icon size={15} style={{ color }} />
      <span className="text-[12px] font-medium text-white leading-tight">{title}</span>
    </div>
    <p className="text-[13px] text-zinc-300 leading-relaxed">{children}</p>
  </div>
);

/* ---------- Phone (handles both simple and stateful moments) ---------- */
const PhoneMoment = ({ p }) => {
  const isStateful = !!p.moment.stateful;
  const [stateIndex, setStateIndex] = useState(0);
  const state = isStateful ? p.moment.states[stateIndex] : null;
  const chat = isStateful ? state.chat : p.moment.chat;
  const chips = isStateful ? state.chips : p.moment.chips;

  return (
    <div className="flex flex-col items-center mx-auto lg:mx-0">
      <div className="mb-3 inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.22em]"
           style={{ background: `${p.color}18`, border: `1px solid ${p.color}55`, color: p.color }}>
        <Sparkles size={11} /> A moment in this experience
      </div>
      <div className="relative w-[248px] h-[510px] bg-black overflow-hidden rounded-[2.7rem] border-[8px]"
           style={{ borderColor: "#18181b", boxShadow: `0 30px 80px rgba(4,12,40,0.55), 0 0 42px ${p.color}22` }}>
        <div className="absolute top-2.5 left-1/2 -translate-x-1/2 z-30 w-20 h-5 bg-black rounded-full" />
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, #071634 0%, #0d2350 45%, #071531 100%)" }} />
          <div className="absolute -top-14 -left-10 w-56 h-56 rounded-full blur-3xl" style={{ background: `${p.color}33` }} />
          <div className="absolute bottom-0 -right-10 w-52 h-52 rounded-full blur-3xl" style={{ background: `${p.color}22` }} />
        </div>
        <div className="absolute left-0 right-0 top-3 z-20 flex items-center justify-between px-6 text-[10px] text-white/70">
          <span>9:41</span><span />
        </div>
        <div className="relative z-10 h-full pt-11 pb-4 px-3.5 flex flex-col">
          <div className="flex items-center justify-between gap-2 mb-1.5 shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-7 h-7 rounded-full shrink-0" style={{ background: `linear-gradient(135deg, ${p.color}, ${p.color}66)` }} />
              <div className="min-w-0">
                <div className="text-white text-[12.5px] font-medium leading-tight">Your Groove</div>
                <div className="text-[9px] leading-tight mt-0.5 flex items-center gap-1" style={{ color: p.color }}>
                  <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: p.color }} />
                  {p.name}
                </div>
              </div>
            </div>
            {isStateful && stateIndex !== 0 && (
              <button type="button" onClick={() => setStateIndex(0)} data-testid="phone-restart"
                      className="inline-flex items-center gap-1 text-[9px] text-zinc-400 hover:text-white transition px-2 py-1 rounded-full bg-white/[0.04] border border-white/10">
                <RotateCcw size={9} /> Restart
              </button>
            )}
          </div>
          <div className="text-[9.5px] uppercase tracking-[0.18em] text-zinc-400 mb-2 shrink-0">{p.moment.setting}</div>

          <div className="flex-1 flex flex-col gap-1.5 overflow-y-auto pr-0.5 mmc-fade">
            {chat.map((m, i) => (
              m.from === "ai" ? (
                <div key={i} className="self-start max-w-[88%] px-3 py-2 rounded-2xl rounded-bl-sm bg-white/[0.06] border border-white/10 text-[11px] text-zinc-100 leading-relaxed">
                  {m.text}
                </div>
              ) : (
                <div key={i} className="self-end max-w-[85%] px-3 py-2 rounded-2xl rounded-br-sm border border-white/10 text-[11px] text-white leading-relaxed"
                     style={{ background: `linear-gradient(135deg, ${p.color}b3, ${p.color}66)` }}>
                  {m.text}
                </div>
              )
            ))}
            {chips && chips.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-1">
                {isStateful ? (
                  chips.map((c) => (
                    <button key={c.label} type="button"
                            onClick={() => c.next !== null && c.next !== undefined && setStateIndex(c.next)}
                            data-testid={`phone-chip-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                            className="text-[10px] px-2.5 py-1 rounded-full bg-white/[0.06] border text-zinc-100 hover:bg-white/[0.12] transition cursor-pointer"
                            style={{ borderColor: `${p.color}55` }}>
                      {c.label}
                    </button>
                  ))
                ) : (
                  chips.map((c) => (
                    <span key={c} className="text-[10px] px-2.5 py-1 rounded-full bg-white/[0.06] border text-zinc-100"
                          style={{ borderColor: `${p.color}55` }}>
                      {c}
                    </span>
                  ))
                )}
              </div>
            )}
          </div>

          <div className="mt-2 shrink-0 flex items-center gap-2 px-3 py-2 rounded-full bg-black/40 border border-white/10">
            <Mic size={12} style={{ color: p.color }} />
            <span className="text-[10.5px] text-zinc-500 flex-1">Speak or type…</span>
            <Send size={11} style={{ color: p.color }} />
          </div>
        </div>
      </div>
      {isStateful && (
        <div className="mt-3 text-[10.5px] text-zinc-500 italic max-w-[248px] text-center leading-snug">
          Interactive — tap a choice to see how the conversation responds.
        </div>
      )}
    </div>
  );
};

/* ---------- Program detail ---------- */
const ProgramDetail = ({ p, index, total, onPrev, onNext, prevProgram, nextProgram, isLast }) => {
  const Icon = p.icon;
  return (
    <div className="mmc-rise glass rounded-3xl p-7 sm:p-9 mt-10" data-testid="program-detail">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 mb-6 pb-5 border-b border-white/10">
        <div className="flex items-center gap-3 min-w-0">
          <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                style={{ background: `${p.color}22`, border: `1px solid ${p.color}66` }}>
            <Icon size={18} style={{ color: p.color }} />
          </span>
          <div className="min-w-0">
            <div className="text-[10.5px] uppercase tracking-[0.24em]" style={{ color: p.color }}>
              Program {index + 1} of {total}
            </div>
            <h3 className="font-heading text-white text-lg sm:text-xl leading-tight truncate">{p.name}</h3>
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-2 shrink-0">
          <button type="button" onClick={onPrev} data-testid="program-prev"
                  className="inline-flex items-center gap-1.5 rounded-full pl-3 pr-4 py-2 bg-white/[0.04] border border-white/10 text-zinc-300 text-[12px] hover:bg-white/[0.08] hover:text-white transition">
            <ChevronLeft size={14} /> Prev
          </button>
          <button type="button" onClick={onNext} data-testid="program-next"
                  className="inline-flex items-center gap-1.5 rounded-full pl-4 pr-3 py-2 text-[12px] text-white font-medium transition"
                  style={{ background: `linear-gradient(135deg, ${p.color}, ${p.color}99)`, boxShadow: `0 0 24px ${p.color}55` }}>
            {isLast ? "Return to first" : "Next"} <ChevronRight size={14} />
          </button>
        </div>
      </div>

      {/* Info grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        <PanelSection icon={Info} title="What it is" color={p.color}>{p.whatIs}</PanelSection>
        <PanelSection icon={CheckCircle2} title="What it accomplishes" color={p.color}>{p.accomplishes}</PanelSection>
        <PanelSection icon={Link2} title="How My Mood Groove connects" color={p.color}>{p.connects}</PanelSection>
        <PanelSection icon={User} title="Example experience" color={p.color}>{p.example}</PanelSection>
      </div>

      {/* Phone + right column */}
      <div className="mt-9 grid lg:grid-cols-[auto_1fr] gap-8 items-start" data-testid="program-moment">
        <PhoneMoment key={p.key} p={p} />

        <div className="flex flex-col gap-3">
          {/* Three-block right column */}
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-5">
              <div className="flex items-center gap-2 mb-2.5">
                <Ear size={14} style={{ color: p.color }} />
                <span className="text-[10.5px] uppercase tracking-[0.2em] text-white">What the nurse experiences</span>
              </div>
              <p className="text-[13px] text-zinc-200 leading-relaxed">{p.moment.nurseExperience}</p>
            </div>
            <div className="rounded-2xl border p-5"
                 style={{ background: `linear-gradient(180deg, ${p.color}12 0%, rgba(255,255,255,0.02) 100%)`, borderColor: `${p.color}44` }}>
              <div className="flex items-center gap-2 mb-2.5">
                <Building2 size={14} style={{ color: p.color }} />
                <span className="text-[10.5px] uppercase tracking-[0.2em]" style={{ color: p.color }}>What ANA receives</span>
              </div>
              <p className="text-[13px] text-zinc-200 leading-relaxed">{p.moment.anaReceives}</p>
            </div>
          </div>

          {/* Privacy boundary */}
          <div className="flex items-start gap-2 rounded-2xl bg-white/[0.02] border border-white/10 px-5 py-4">
            <Lock size={13} className="text-zinc-400 mt-0.5 shrink-0" />
            <p className="text-[12.5px] text-zinc-300 leading-relaxed">
              <span className="text-white font-medium">Privacy boundary. </span>
              {p.moment.privacy}
            </p>
          </div>

          {/* Connection panel — dedicated */}
          <div className="rounded-2xl border p-5"
               style={{ background: `linear-gradient(180deg, ${p.color}0f 0%, rgba(255,255,255,0.015) 100%)`, borderColor: `${p.color}55` }}>
            <div className="flex items-center gap-2 mb-2.5">
              <Radio size={14} style={{ color: p.color }} />
              <span className="text-[10.5px] uppercase tracking-[0.2em]" style={{ color: p.color }}>
                How this moment connects to {p.name}
              </span>
            </div>
            <p className="text-[13.5px] text-zinc-100 leading-relaxed">{p.moment.connectionPanel}</p>
          </div>

          {/* Bottom advance bar */}
          <div className="mt-2 pt-4 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3">
            <button type="button" onClick={onPrev} data-testid="program-prev-bottom"
                    className="inline-flex items-center gap-1.5 rounded-full pl-3 pr-5 py-2.5 bg-white/[0.04] border border-white/10 text-zinc-300 text-[12.5px] hover:bg-white/[0.08] hover:text-white transition">
              <ChevronLeft size={15} />
              <span className="text-left">
                <span className="block text-[9px] uppercase tracking-[0.2em] text-zinc-500">Previous program</span>
                <span className="block leading-tight">{prevProgram.name}</span>
              </span>
            </button>
            <div className="flex items-center gap-1.5" aria-hidden="true">
              {Array.from({ length: total }).map((_, di) => (
                <span key={di} className="rounded-full transition-all"
                      style={{ width: di === index ? 22 : 6, height: 6,
                               background: di === index ? p.color : "rgba(255,255,255,0.15)",
                               boxShadow: di === index ? `0 0 10px ${p.color}88` : "none" }} />
              ))}
            </div>
            <button type="button" onClick={onNext} data-testid="program-next-bottom"
                    className="inline-flex items-center gap-1.5 rounded-full pl-5 pr-3 py-2.5 text-[12.5px] text-white font-medium transition"
                    style={{ background: `linear-gradient(135deg, ${p.color}, ${p.color}99)`, boxShadow: `0 0 28px ${p.color}55` }}>
              <span className="text-right">
                <span className="block text-[9px] uppercase tracking-[0.2em] text-white/70">
                  {isLast ? "Return to first program" : "Next program"}
                </span>
                <span className="block leading-tight">{nextProgram.name}</span>
              </span>
              <ChevronRight size={15} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---------- Main component ---------- */
const ProgramMindMap = () => {
  const [selected, setSelected] = useState(0);
  const [stickyOn, setStickyOn] = useState(false);
  const sectionRef = useRef(null);
  const cardsRef = useRef(null);
  const total = programs.length;
  const go = (i) => setSelected(((i % total) + total) % total);

  useEffect(() => {
    const cards = cardsRef.current;
    const section = sectionRef.current;
    if (!cards || !section || typeof IntersectionObserver === "undefined") return;
    let cardsOut = false; let sectionIn = false;
    const update = () => setStickyOn(cardsOut && sectionIn);
    const o1 = new IntersectionObserver(([e]) => { cardsOut = !e.isIntersecting; update(); }, { rootMargin: "-80px 0px 0px 0px" });
    const o2 = new IntersectionObserver(([e]) => { sectionIn = e.isIntersecting; update(); }, { threshold: 0 });
    o1.observe(cards); o2.observe(section);
    return () => { o1.disconnect(); o2.disconnect(); };
  }, []);

  return (
    <div data-testid="program-mindmap" ref={sectionRef}>
      <StickySelector selected={selected} onSelect={go} visible={stickyOn} />

      <div className="flex flex-wrap items-center justify-center gap-2 mb-6 text-[12px] text-zinc-300">
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.04] border border-teal-300/25">
          <span className="w-1.5 h-1.5 rounded-full bg-teal-300 mmc-blink" />
          Select a program to experience how My Mood Groove brings it into the nurse&rsquo;s daily life.
        </span>
      </div>

      <div ref={cardsRef} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="program-selector-grid">
        {programs.map((p, i) => (
          <SelectorCard key={p.key} p={p} isActive={selected === i} onClick={() => go(i)} />
        ))}
      </div>

      <ProgramDetail
        key={programs[selected].key}
        p={programs[selected]}
        index={selected}
        total={total}
        isLast={selected === total - 1}
        onPrev={() => go(selected - 1)}
        onNext={() => go(selected + 1)}
        prevProgram={programs[(selected - 1 + total) % total]}
        nextProgram={programs[(selected + 1) % total]}
      />

      {/* Section footer */}
      <div className="mt-12 max-w-3xl mx-auto text-center" data-testid="program-footer">
        <h4 className="font-heading text-white text-xl sm:text-2xl leading-snug">
          The nurse owns the conversation.
        </h4>
        <p className="mt-3 text-[14px] text-zinc-300 leading-relaxed">
          Personal reflections and individual choices remain private. With consent and appropriate safeguards,
          ANA may receive only deidentified, aggregated patterns that can help improve programs and resource access.
        </p>
      </div>

      <style>{`
        .mmc-rise{ animation: mmcRise .45s ease both; }
        @keyframes mmcRise{ from{ opacity:0; transform:translateY(14px);} to{ opacity:1; transform:none;} }
        .mmc-blink{ animation: mmcBlink 1.6s ease-in-out infinite; box-shadow: 0 0 10px #2dd4bf; }
        @keyframes mmcBlink{ 0%,100%{opacity:1;} 50%{opacity:.35;} }
        .mmc-line{ animation: mmcLine 2.4s ease-in-out infinite; }
        @keyframes mmcLine{ 0%,100%{ opacity:.4; } 50%{ opacity:1; } }
        .mmc-fade{ animation: mmcFade .35s ease both; }
        @keyframes mmcFade{ from{ opacity:.4; } to{ opacity:1; } }
        @media (prefers-reduced-motion: reduce){
          .mmc-rise,.mmc-blink,.mmc-line,.mmc-fade{ animation:none; }
        }
      `}</style>
    </div>
  );
};

export default ProgramMindMap;
